/**
 * A agenda como PAUTA: uma linha por sessão de julgamento, que se abre em
 * relator e processos; o que não é sessão (audiência, prazo, reunião, evento
 * do Google) em linha própria. Porte do branch do Rubens (`2c1221bc`,
 * `2e323de8`, `2ad23404`). O agrupamento e as frases vivem em
 * `lib/calendario/sessoes.ts`; aqui só o desenho.
 *
 * As sessões começam recolhidas: a lista mostra as sessões, e cada uma se
 * abre no clique. Dentro dela, cada processo abre o detalhe do evento
 * (editar, excluir, ver a intimação) e tem o atalho para o processo.
 */
import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { CalendarEvent } from '@/api/client';
import { Button } from '@/components/ui/button';
import { GoogleIcon } from '@/components/ui/GoogleIcon';
import { formatNumeroProcesso, toLocalISODate, toTitleCaseNome } from '@/lib/formatters';
import {
  agruparPauta,
  diaMes,
  estadoDaSessao,
  horaFalada,
  quandoDaSessao,
  tituloDaSessao,
  type EventoAvulso,
  type SessaoNaPauta,
} from '@/lib/calendario/sessoes';
import { SEM_HORA, SEM_HORA_EXPLICACAO } from '@/lib/eventoHorario';
import { naSemanaCorrente } from '@/lib/semana';
import { cn } from '@/lib/utils';
import { DaAgendaDe } from './DaAgendaDe';
import { LinkDosAutos } from '@/components/processos/LinkDosAutos';

/** O bloco da data: o número do dia e, embaixo, "hoje", o dia da semana (só
 *  na semana corrente) ou o mês. A lista vai a seis meses: "3 · ter" seria
 *  ambíguo entre outubro e novembro. */
function rotuloDoDia(iso: string, hojeIso: string): { dia: string; sub: string } {
  const dia = toLocalISODate(iso);
  const d = new Date(`${dia}T12:00:00`);
  const opcao: Intl.DateTimeFormatOptions = naSemanaCorrente(d, new Date()) ? { weekday: 'short' } : { month: 'short' };
  const sub = dia === hojeIso ? 'hoje' : d.toLocaleDateString('pt-BR', opcao).replace(/\.$/, '');
  return { dia: String(d.getDate()), sub };
}

const ROTULO_AVULSO: Record<string, string> = {
  audiencia: 'Audiência',
  prazo: 'Prazo',
  reuniao: 'Reunião',
  sessao: 'Sessão de julgamento',
  outro: 'Compromisso',
};

/** O dia em número grande, sem caixa. Hoje, na cor primária. */
function BlocoDoDia({ dia, sub, hoje }: { dia: string; sub: string; hoje: boolean }) {
  return (
    <span className="flex w-14 shrink-0 flex-col items-start">
      <span className={cn('text-2xl font-bold leading-none tabular-nums', hoje ? 'text-primary' : 'text-foreground')}>
        {dia}
      </span>
      <span className={cn('text-xs leading-tight', hoje ? 'font-bold text-primary' : 'text-muted-foreground')}>
        {sub}
      </span>
    </span>
  );
}

/** Modalidade: ponto de cor + palavra. Dourado = virtual; tinta = presencial. */
function Selo({ texto, tom }: { texto: string; tom: 'ouro' | 'neutro' }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
      <span
        aria-hidden="true"
        className={cn('h-2 w-2 rounded-full', tom === 'ouro' ? 'bg-[var(--color-gold)]' : 'bg-foreground/70')}
      />
      {texto}
    </span>
  );
}

/** O evento também está no Google Calendar do titular. */
function SeloGoogle() {
  return (
    <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
      <GoogleIcon className="h-2.5 w-2.5" />
      Google
    </span>
  );
}

function LinhaDeSessao({
  s,
  hojeIso,
  aberta,
  alternar,
  onSelectEvent,
}: {
  s: SessaoNaPauta;
  hojeIso: string;
  aberta: boolean;
  alternar: () => void;
  onSelectEvent: (e: CalendarEvent) => void;
}) {
  const { dia, sub } = rotuloDoDia(s.inicio, hojeIso);
  const estado = estadoDaSessao(s, hojeIso);
  const destaque = estado === 'hoje' || estado === 'encerra hoje' || estado === 'em curso';
  const painelId = `sessao-${s.chave.replace(/[^a-z0-9]/gi, '')}`;
  const titulo = tituloDaSessao(s);

  return (
    <li className="border-t border-border first:border-t-0">
      <Button
        type="button"
        variant="unstyled"
        onClick={alternar}
        aria-expanded={aberta}
        aria-controls={aberta ? painelId : undefined}
        aria-label={`${titulo}, ${quandoDaSessao(s, hojeIso)}: ${aberta ? 'recolher' : 'ver'} relator e processos`}
        className="grid h-auto min-h-16 w-full grid-cols-[3.5rem_minmax(0,1fr)_auto] items-center gap-3 whitespace-normal rounded-lg px-1 py-3 text-left font-normal hover:bg-accent/50 sm:gap-4"
      >
        <BlocoDoDia dia={dia} sub={s.fim ? `até ${diaMes(s.fim)}` : sub} hoje={toLocalISODate(s.inicio) === hojeIso} />
        <span className="flex min-w-0 flex-col gap-1">
          <span className="break-words text-base font-bold leading-snug">{titulo}</span>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm leading-snug text-muted-foreground">
            {s.modalidade && (
              <Selo
                texto={s.modalidade === 'virtual' ? 'Virtual' : 'Presencial'}
                tom={s.modalidade === 'virtual' ? 'ouro' : 'neutro'}
              />
            )}
            <span>{quandoDaSessao(s, hojeIso)}</span>
          </span>
          {s.modalidade === 'presencial' && s.local && (
            <span className="break-words text-xs text-muted-foreground">{s.local}</span>
          )}
        </span>
        <span className="flex items-center gap-2">
          <span className="flex flex-col items-end gap-0.5">
            <span className={cn('text-xs font-semibold', destaque ? 'text-primary' : 'text-muted-foreground')}>
              {estado}
            </span>
            <span className="whitespace-nowrap text-xs text-muted-foreground">
              {s.total === 1 ? '1 processo' : `${s.total} processos`}
            </span>
          </span>
          <ChevronDown
            aria-hidden="true"
            className={cn('h-4 w-4 shrink-0 text-primary transition-transform', aberta && 'rotate-180')}
          />
        </span>
      </Button>

      {aberta && (
        <div id={painelId} className="mb-4 ml-1 flex flex-col gap-3 sm:ml-[4.5rem]">
          {s.relatores.map((r) => (
            <div key={r.nome}>
              <p className="mb-1.5 text-sm text-muted-foreground">
                Relator: <span className="font-semibold text-foreground">{r.nome}</span>
              </p>
              <ul className="overflow-hidden rounded-md border border-border bg-card">
                {r.processos.map((p) => (
                  <li
                    key={p.eventoId}
                    className="flex items-stretch border-t border-border first:border-t-0"
                  >
                    <Button
                      type="button"
                      variant="unstyled"
                      onClick={() => onSelectEvent(p.evento)}
                      className="flex h-auto min-h-11 min-w-0 flex-1 flex-col items-start gap-0.5 whitespace-normal rounded-none px-3 py-2.5 text-left font-normal hover:bg-accent/60"
                    >
                      <span className="break-words text-sm font-semibold leading-snug">
                        {p.partes ? toTitleCaseNome(p.partes) : p.classe ?? 'Processo'}
                      </span>
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 break-words text-xs tabular-nums text-muted-foreground">
                        <span>
                          {p.partes && p.classe ? `${p.classe} · ` : ''}
                          {p.numero ? formatNumeroProcesso(p.numero) : ''}
                        </span>
                        {p.evento.google_event_id && <SeloGoogle />}
                        <DaAgendaDe evento={p.evento} />
                      </span>
                    </Button>
                    {p.numero && (
                      <LinkDosAutos
                        numero={p.numero}
                        className="flex min-h-11 shrink-0 items-center px-3 text-xs font-semibold text-primary hover:bg-accent/60"
                      >
                        Autos
                      </LinkDosAutos>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {!s.colegiado && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              A intimação não diz qual Turma julga
              {s.local ? <>; diz só a sala, “{s.local}”</> : null}.
            </p>
          )}
        </div>
      )}
    </li>
  );
}

function LinhaAvulsa({
  item,
  hojeIso,
  onSelect,
}: {
  item: EventoAvulso;
  hojeIso: string;
  onSelect: (e: CalendarEvent) => void;
}) {
  const e = item.evento;
  const { dia, sub } = rotuloDoDia(e.data_inicio, hojeIso);
  const semHora = e.hora_conhecida === false;
  return (
    <li className="border-t border-border first:border-t-0">
      <Button
        type="button"
        variant="unstyled"
        onClick={() => onSelect(e)}
        className="grid h-auto min-h-16 w-full grid-cols-[3.5rem_minmax(0,1fr)] items-center gap-3 whitespace-normal rounded-lg px-1 py-3 text-left font-normal hover:bg-accent/50 sm:gap-4"
      >
        <BlocoDoDia dia={dia} sub={sub} hoje={toLocalISODate(e.data_inicio) === hojeIso} />
        <span className="flex min-w-0 flex-col gap-1">
          <span className="break-words text-base font-bold leading-snug">{e.titulo}</span>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            <Selo texto={ROTULO_AVULSO[e.tipo] ?? 'Compromisso'} tom="neutro" />
            {semHora ? (
              <span className="italic" title={SEM_HORA_EXPLICACAO}>
                {SEM_HORA}
              </span>
            ) : (
              <span>às {horaFalada(e.data_inicio)}</span>
            )}
            {e.parte_principal && <span className="break-words">{toTitleCaseNome(e.parte_principal)}</span>}
            {e.local && <span className="break-words">{e.local}</span>}
            {e.google_event_id && <SeloGoogle />}
            <DaAgendaDe evento={e} />
          </span>
        </span>
      </Button>
    </li>
  );
}

export function PautaDeSessoes({
  events,
  onSelectEvent,
  recentesPrimeiro = false,
}: {
  events: CalendarEvent[];
  onSelectEvent: (e: CalendarEvent) => void;
  /** Olhando para trás ("Últimos 7 dias"): o mais recente no alto. */
  recentesPrimeiro?: boolean;
}) {
  const hojeIso = toLocalISODate(new Date().toISOString());
  const itens = useMemo(() => agruparPauta(events, { recentesPrimeiro }), [events, recentesPrimeiro]);
  const [abertas, setAbertas] = useState<Set<string>>(() => new Set());
  const alternar = (chave: string) => {
    setAbertas((atuais) => {
      const nova = new Set(atuais);
      if (nova.has(chave)) nova.delete(chave);
      else nova.add(chave);
      return nova;
    });
  };

  const sessoes = itens.filter((i) => i.tipo === 'sessao').length;
  const processos = itens.reduce((t, i) => t + (i.tipo === 'sessao' ? i.total : 0), 0);

  return (
    <>
      {sessoes > 0 && (
        <p className="mb-1 text-sm text-muted-foreground">
          {processos === 1 ? '1 processo pautado' : `${processos} processos pautados`} em{' '}
          {sessoes === 1 ? '1 sessão' : `${sessoes} sessões`}.
        </p>
      )}
      <ul>
        {itens.map((i) =>
          i.tipo === 'sessao' ? (
            <LinhaDeSessao
              key={i.chave}
              s={i}
              hojeIso={hojeIso}
              aberta={abertas.has(i.chave)}
              alternar={() => alternar(i.chave)}
              onSelectEvent={onSelectEvent}
            />
          ) : (
            <LinhaAvulsa key={i.chave} item={i} hojeIso={hojeIso} onSelect={onSelectEvent} />
          ),
        )}
      </ul>
    </>
  );
}
